import React, { useState } from 'react';
import { 
  Inbox, 
  Tag, 
  FlaskConical, 
  Edit3, 
  Layers, 
  Rocket, 
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Info
} from 'lucide-react';
import { processPhases, processInsights } from '../data/processFlow';

const ProcessFlowHero: React.FC = () => {
  const [expandedPhase, setExpandedPhase] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  const getIcon = (iconName: string) => {
    const icons: Record<string, any> = {
      Inbox,
      Tag,
      FlaskConical,
      Edit3,
      Layers,
      Rocket
    };
    return icons[iconName] || Inbox;
  };

  const getColorClasses = (color: string) => {
    const colors: Record<string, any> = {
      blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-900',
        accent: 'text-blue-600',
        badge: 'bg-blue-100 text-blue-800'
      },
      green: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        text: 'text-green-900',
        accent: 'text-green-600',
        badge: 'bg-green-100 text-green-800'
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        text: 'text-purple-900',
        accent: 'text-purple-600',
        badge: 'bg-purple-100 text-purple-800'
      },
      orange: {
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        text: 'text-orange-900',
        accent: 'text-orange-600',
        badge: 'bg-orange-100 text-orange-800'
      },
      amber: {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        text: 'text-amber-900',
        accent: 'text-amber-600',
        badge: 'bg-amber-100 text-amber-800'
      },
      red: {
        bg: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-900',
        accent: 'text-red-600',
        badge: 'bg-red-100 text-red-800'
      }
    };
    return colors[color] || colors.blue;
  };

  const togglePhase = (phaseId: string) => {
    setExpandedPhase(expandedPhase === phaseId ? null : phaseId);
  };

  return (
    <div className="bg-gradient-to-br from-primary-50 via-sage-50 to-amber-50 rounded-2xl p-8 mb-8 border border-primary-200">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-display font-bold text-charcoal-900 mb-2">
          {processInsights.title}
        </h2>
        {processInsights.subtitle && (
          <p className="text-xl font-display text-charcoal-600 mb-6">
            {processInsights.subtitle}
          </p>
        )}

        {/* Toggle Details Button */}
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-charcoal-200 text-charcoal-700 hover:bg-charcoal-50 transition-colors"
        >
          <Info className="h-4 w-4" />
          {showDetails ? 'Hide Details' : 'Show Details'}
          {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>

      {/* Process Flow */}
      <div className="relative">
        {/* Desktop Flow */}
        <div className="hidden lg:grid grid-cols-6 gap-4">
          {processPhases.map((phase, index) => {
            const Icon = getIcon(phase.icon);
            const colors = getColorClasses(phase.color);
            
            return (
              <React.Fragment key={phase.id}>
                <div
                  className={`cursor-pointer transition-all duration-300 hover:scale-105 ${colors.bg} ${colors.border} border-2 rounded-xl p-6 ${expandedPhase === phase.id ? 'ring-2 ring-primary-300' : ''}`}
                  onClick={() => togglePhase(phase.id)}
                >
                  <div className="text-center">
                    <div className={`inline-flex items-center justify-center w-12 h-12 ${colors.badge} rounded-lg mb-4`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className={`font-bold text-lg ${colors.text} mb-2`}>
                      {phase.verb}
                    </h3>
                    <p className={`text-sm ${colors.text} mb-3`}>
                      {phase.description}
                    </p>
                    <div className="flex flex-wrap justify-center gap-1">
                      {phase.engines.map((engine, engineIndex) => (
                        <span
                          key={engineIndex}
                          className="px-2 py-1 bg-white rounded text-xs text-charcoal-600 border"
                        >
                          {engine.name}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* Mobile Flow */}
        <div className="lg:hidden space-y-4">
          {processPhases.map((phase, index) => {
            const Icon = getIcon(phase.icon);
            const colors = getColorClasses(phase.color);
            const isExpanded = expandedPhase === phase.id;
            
            return (
              <div key={phase.id}>
                <div
                  className={`cursor-pointer transition-all duration-300 ${colors.bg} ${colors.border} border-2 rounded-xl p-4 ${isExpanded ? 'ring-2 ring-primary-300' : ''}`}
                  onClick={() => togglePhase(phase.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`inline-flex items-center justify-center w-10 h-10 ${colors.badge} rounded-lg`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className={`font-bold ${colors.text}`}>
                          {phase.verb}
                        </h3>
                        <p className={`text-sm ${colors.text}`}>
                          {phase.description}
                        </p>
                      </div>
                    </div>
                    {isExpanded ? <ChevronUp className="h-5 w-5 text-charcoal-400" /> : <ChevronDown className="h-5 w-5 text-charcoal-400" />}
                  </div>
                </div>
                
                {isExpanded && (
                  <div className={`mt-2 ${colors.bg} rounded-lg p-4 border ${colors.border}`}>
                    <div className="space-y-3">
                      <div>
                        <h4 className="font-semibold text-charcoal-900 mb-2">Details:</h4>
                        <p className="text-sm text-charcoal-700">{phase.details}</p>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-charcoal-900 mb-2">Examples:</h4>
                        <ul className="text-sm text-charcoal-700 space-y-1">
                          {phase.examples.map((example, exampleIndex) => (
                            <li key={exampleIndex} className="flex items-start gap-2">
                              <span className="text-charcoal-400">•</span>
                              <span>{example}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-charcoal-900 mb-2">Engines:</h4>
                        <div className="flex flex-wrap gap-2">
                          {phase.engines.map((engine, engineIndex) => (
                            <span
                              key={engineIndex}
                              className="px-2 py-1 bg-white rounded text-xs text-charcoal-600 border"
                            >
                              {engine.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {index < processPhases.length - 1 && (
                  <div className="flex justify-center my-4">
                    <ArrowRight className="h-6 w-6 text-charcoal-400 rotate-90" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Expanded Details for Desktop */}
        {showDetails && expandedPhase && (
          <div className="mt-8 bg-white rounded-xl p-6 border border-charcoal-200">
            {(() => {
              const phase = processPhases.find(p => p.id === expandedPhase);
              if (!phase) return null;
              
              return (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-4">
                    {(() => {
                      const Icon = getIcon(phase.icon);
                      const colors = getColorClasses(phase.color);
                      return (
                        <div className={`inline-flex items-center justify-center w-10 h-10 ${colors.badge} rounded-lg`}>
                          <Icon className="h-5 w-5" />
                        </div>
                      );
                    })()}
                    <h3 className="text-xl font-bold text-charcoal-900">{phase.title}</h3>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-charcoal-900 mb-2">Details:</h4>
                      <p className="text-charcoal-700">{phase.details}</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-charcoal-900 mb-2">Examples:</h4>
                      <ul className="text-charcoal-700 space-y-1">
                        {phase.examples.map((example, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-charcoal-400">•</span>
                            <span>{example}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Engines and Operations */}
                  <div>
                    <h4 className="font-semibold text-charcoal-900 mb-4">Engines & Operations:</h4>
                    <div className="space-y-4">
                      {phase.engines.map((engine) => (
                        <div key={engine.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                            <h5 className="font-semibold text-charcoal-900">{engine.name}</h5>
                            <span className="text-sm text-charcoal-600">({engine.purpose})</span>
                          </div>
                          
                          <div className="ml-4 space-y-2">
                            {engine.operations.map((operation) => (
                              <div key={operation.id} className="bg-white rounded p-3 border border-gray-200">
                                <div className="flex items-start justify-between mb-2">
                                  <div>
                                    <code className="text-sm font-mono text-primary-700 bg-primary-50 px-2 py-1 rounded">
                                      {operation.name}
                                    </code>
                                    <p className="text-sm text-charcoal-700 mt-1">{operation.description}</p>
                                  </div>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                  <div>
                                    <span className="font-medium text-charcoal-600">Input:</span>
                                    <code className="block text-charcoal-500 mt-1">{operation.inputSchema}</code>
                                  </div>
                                  <div>
                                    <span className="font-medium text-charcoal-600">Output:</span>
                                    <code className="block text-charcoal-500 mt-1">{operation.outputSchema}</code>
                                  </div>
                                </div>
                                
                                <div className="mt-2">
                                  <span className="font-medium text-charcoal-600 text-xs">Example:</span>
                                  <code className="block text-xs text-charcoal-500 mt-1 bg-gray-100 p-2 rounded">
                                    {operation.example}
                                  </code>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProcessFlowHero;
